/**
 * CallCentreAdminService
 * Console surface of Call Centre Management ("Call campaigns", /app/callcentre): dashboard, reports (+ CSV),
 * audit, campaigns, assignment, imports, contacts, do not call, detail changes, journey (DA, E1 to E5),
 * handoffs, ministry owners, callers, reminders, settings, outcomes and campaign types.
 * Route: callcentre/api/callcentre/admin (roles Admin,CallCentreAdmin,CallCentreSupervisor + per-action rights,
 * see CALL_CENTRE_RIGHTS), plus `getMemberJourney` on callcentre/api/callcentre/journey/members/{memberId}
 * (Member.Read OR CallCentre.Read) for the member profile's Journey card.
 *
 * Imports: startImport -> addImportRows in chunks of at most CALL_CENTRE_LIMITS.ImportRowsPerRequest (500,
 * `isLastChunk: true` on the last) -> review (listImportRows / decideImportRow / bulkImportDecision) -> commitImport.
 *
 * Stage 2 list sources: listPcoSources / listPcoFormSources (+ previewPcoFormSource) / listFormSources /
 * listCourseDateSources -> importFromSource (the server
 * fetches the rows; the batch comes back Ready for review) -> the same review and commitImport. Planning Centre
 * linking: commitImport `linkPlanningCentre`, or linkCampaignPco, then follow listSyncJobs / getSyncJob
 * (retrySyncJob, cancelSyncJob); per person findPersonPco -> linkPersonPco, or createPersonPco.
 *
 * Stage 3 phone system (Grandstream UCM click-to-call): getTelephony / saveTelephony (blank password keeps the
 * stored one), testTelephony (sign in) and diagnoseTelephony (DNS, TCP, TLS, API, sign in) return the steps;
 * listCdr / syncCdr for call records; listStations / createStation / updateStation / removeStation (switch off)
 * for desk phones.
 *
 * Stage 4 journey integrations: listCourseSources (Acts Care courses for a step's booking target, ref = CourseID),
 * getStepCourseDates (the upcoming dates of a course-linked step; book one with linkedCourseDateID + hasConsent),
 * backfillJourneyStep (earlier Acts Care completions, idempotent), and the Planning Centre pickers for a step's sync
 * target: listPcoFieldDefinitions (Custom field mode, ref = field id) and listPcoWorkflows (Workflow card mode,
 * ref = workflow id or a step's "workflowId:stepId").
 *
 * Stage 5 notifications, integrations and teams: getNotificationTopics / saveNotificationTopics (per-topic email,
 * SMS and WhatsApp switches) and sendTestNotification (to the signed-in admin); sendHandoffToCare (a Care referral
 * becomes an Acts Care request; read `integrationMessage` on the result) and setHandoffStatus `joined` (Ministry
 * referrals marked Done); caller teams (listTeams / createTeam / updateTeam / setTeamMembers / deleteTeam),
 * addCampaignTeam, and assignBalanced `teamID` / `byCampus` / `campusOnly`.
 *
 * 271m results and messages: getCampaignResults / saveCampaignResults (which call results a campaign's callers see),
 * addCampaignMessage / saveCampaignMessage / deleteCampaignMessage / addSuggestedMessages (messages to the person called
 * after a result; suggested ones arrive switched off), listMessagePresets / addMessagePreset / saveMessagePreset /
 * deleteMessagePreset (the library) and listContactMessages (what was sent to a contact).
 *
 * 271j member fields: getMemberFieldSettings / saveMemberFieldSettings (which member fields callers may add when missing
 * and / or update on a call); listDetailChanges carries the member field key, kind, contact use and every value.
 *
 * Stage 6 AI assist: getAiAvailability (which helpers this admin can use), aiScriptDraft (Campaigns.Manage; a draft
 * script with notes, nothing saved), aiImportMap (Import; a suggested field per spreadsheet column, only header text
 * and value shapes reach the model) and getAiInsights (Admin / CallCentreAdmin or CallCentre.AI.Use; themes, prayer
 * themes and coaching hints from recent non-pastoral notes, cached 6 hours). Each answers 503 `ai_unavailable` while
 * its flag or the AI switch is off (hide the action) and 503 `ai_failed` when the AI service failed (carry on).
 *
 * Build with AI for conversation guides (aimanagement 009, Anthropic Claude on Amazon Bedrock): startCampaignGuideAiDraft
 * / startCampaignGuideAiRevise / startCampaignTypeGuideAiDraft / startCampaignTypeGuideAiRevise start a background job;
 * getGuideAiJob polls it. The draft is never saved: it lands in the builder for a person to check and save.
 */
import { BaseService } from '../base/BaseService';
import { unwrapCallCentreResponse as unwrap, callCentreOkOrSuccess as okOrSuccess, callCentreListOrEmpty as listOrEmpty, } from './unwrapCallCentreResponse';
/** Call Centre console service (JWT + console role + rights). */
export class CallCentreAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CallCentreAdminService.DEFAULT_SERVICE_PATH,
            basePath: CallCentreAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    adminUrl(endpoint, params) {
        return this.buildUrlWithParams(`admin/${endpoint}`, params);
    }
    static reportParams(query = {}) {
        return { from: query.from, to: query.to, campaignId: query.campaignId, userId: query.userId };
    }
    static contactParams(query = {}, includeCampaign = true) {
        return {
            campaignId: includeCampaign ? query.campaignId : undefined,
            statusId: query.statusId,
            openOnly: query.openOnly,
            assignedTo: query.assignedTo,
            outcomeId: query.outcomeId,
            memberId: query.memberId,
            search: query.search,
            page: query.page,
            pageSize: query.pageSize,
        };
    }
    // ---- dashboard, reports, audit ----
    /** GET admin/dashboard (Read) */
    async getDashboard() {
        return unwrap(await this.getDashboardRaw(), 'Call centre dashboard not found');
    }
    getDashboardRaw() {
        return this.http.get(this.adminUrl('dashboard'));
    }
    /** GET admin/reports?from=&to=&campaignId=&userId= (Read) */
    async getReport(query = {}) {
        return unwrap(await this.getReportRaw(query), 'Report not found');
    }
    getReportRaw(query = {}) {
        return this.http.get(this.adminUrl('reports', CallCentreAdminService.reportParams(query)));
    }
    /** GET admin/reports/attempts/export (Contacts.Export, audited): the filtered call attempts as raw text/csv. */
    exportAttemptsCsv(query = {}) {
        return this.http.getBlob(this.adminUrl('reports/attempts/export', CallCentreAdminService.reportParams(query)));
    }
    /** GET admin/audit?page=&pageSize=&entityType= (Settings.Manage) */
    async getAudit(query = {}) {
        return unwrap(await this.getAuditRaw(query), 'Audit not found');
    }
    getAuditRaw(query = {}) {
        return this.http.get(this.adminUrl('audit', { page: query.page, pageSize: query.pageSize, entityType: query.entityType }));
    }
    // ---- campaigns ----
    /** GET admin/campaigns?statusId=&search=&includeArchived= (Read) */
    async listCampaigns(query = {}) {
        return listOrEmpty(await this.listCampaignsRaw(query));
    }
    listCampaignsRaw(query = {}) {
        return this.http.get(this.adminUrl('campaigns', { statusId: query.statusId, search: query.search, includeArchived: query.includeArchived }));
    }
    /** GET admin/campaigns/{id} (Read) */
    async getCampaign(campaignId) {
        return unwrap(await this.getCampaignRaw(campaignId), `Campaign ${campaignId} not found`);
    }
    getCampaignRaw(campaignId) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}`));
    }
    /** POST admin/campaigns (Campaigns.Manage): create a draft. */
    async createCampaign(request) {
        return unwrap(await this.createCampaignRaw(request), 'Failed to create the campaign');
    }
    createCampaignRaw(request) {
        return this.http.post(this.adminUrl('campaigns'), request);
    }
    /** PUT admin/campaigns/{id} (Campaigns.Manage) */
    async updateCampaign(campaignId, request) {
        return unwrap(await this.updateCampaignRaw(campaignId, request), 'Failed to save the campaign');
    }
    updateCampaignRaw(campaignId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}`), request);
    }
    /**
     * POST admin/campaigns/{id}/status (Campaigns.Manage): Draft -> Active (launch), Active <-> Paused,
     * Active/Paused -> Completed, any -> Archived, Archived -> Draft (restore).
     */
    async setCampaignStatus(campaignId, request) {
        return unwrap(await this.setCampaignStatusRaw(campaignId, request), 'Failed to change the campaign status');
    }
    setCampaignStatusRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/status`), request);
    }
    /** DELETE admin/campaigns/{id} (Campaigns.Manage): soft delete. */
    async deleteCampaign(campaignId) {
        return okOrSuccess(await this.deleteCampaignRaw(campaignId));
    }
    deleteCampaignRaw(campaignId) {
        return this.http.delete(this.adminUrl(`campaigns/${campaignId}`));
    }
    /** PUT admin/campaigns/{id}/callers (Assign): the full caller list (left-out callers are deactivated). */
    async saveCampaignCallers(campaignId, request) {
        return listOrEmpty(await this.saveCampaignCallersRaw(campaignId, request));
    }
    saveCampaignCallersRaw(campaignId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}/callers`), request);
    }
    /** POST admin/campaigns/{id}/callers/team/{teamId} (Assign): adds every active caller in the team to the campaign. */
    async addCampaignTeam(campaignId, teamId) {
        return unwrap(await this.addCampaignTeamRaw(campaignId, teamId), 'Failed to add the team');
    }
    addCampaignTeamRaw(campaignId, teamId) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/callers/team/${teamId}`), {});
    }
    /** PUT admin/campaigns/{id}/questions (Campaigns.Manage): the full list in order (left-out questions are switched off). */
    async saveCampaignQuestions(campaignId, request) {
        return listOrEmpty(await this.saveCampaignQuestionsRaw(campaignId, request));
    }
    saveCampaignQuestionsRaw(campaignId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}/questions`), request);
    }
    /** GET admin/campaigns/{id}/contacts (Read): the campaign's contacts (query.campaignId is ignored). */
    async listCampaignContacts(campaignId, query = {}) {
        return unwrap(await this.listCampaignContactsRaw(campaignId, query), 'Contacts not found');
    }
    listCampaignContactsRaw(campaignId, query = {}) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/contacts`, CallCentreAdminService.contactParams(query, false)));
    }
    /** POST admin/campaigns/{id}/contacts (Import): add one person by hand (or from a CCMS member). */
    async addCampaignContact(campaignId, request) {
        return unwrap(await this.addCampaignContactRaw(campaignId, request), 'Failed to add the contact');
    }
    addCampaignContactRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/contacts`), request);
    }
    /** GET admin/campaigns/{id}/contacts/export (Contacts.Export, audited): the filtered contacts as raw text/csv. */
    exportCampaignContactsCsv(campaignId, query = {}) {
        return this.http.getBlob(this.adminUrl(`campaigns/${campaignId}/contacts/export`, CallCentreAdminService.contactParams(query, false)));
    }
    /** GET admin/campaigns/{id}/workload (Read): unassigned / open counts and each caller's load. */
    async getCampaignWorkload(campaignId) {
        return unwrap(await this.getCampaignWorkloadRaw(campaignId), 'Workload not found');
    }
    getCampaignWorkloadRaw(campaignId) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/workload`));
    }
    /** POST admin/campaigns/{id}/assign (Assign): give these contacts to one caller. */
    async assignContacts(campaignId, request) {
        return unwrap(await this.assignContactsRaw(campaignId, request), 'Failed to assign the contacts');
    }
    assignContactsRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/assign`), request);
    }
    /**
     * POST admin/campaigns/{id}/assign/balanced (Assign): spread unassigned contacts (fewest open first, respecting maxOpen).
     * `teamID` = only that team's members; `byCampus` = callers at the person's campus first; `campusOnly` (with byCampus)
     * leaves people whose campus has no caller with room unassigned.
     */
    async assignBalanced(campaignId, request = {}) {
        return unwrap(await this.assignBalancedRaw(campaignId, request), 'Failed to balance the contacts');
    }
    assignBalancedRaw(campaignId, request = {}) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/assign/balanced`), request);
    }
    /** POST admin/campaigns/{id}/release (Assign): back to unassigned (a list, or every open contact of fromUserId). */
    async releaseContacts(campaignId, request = {}) {
        return unwrap(await this.releaseContactsRaw(campaignId, request), 'Failed to release the contacts');
    }
    releaseContactsRaw(campaignId, request = {}) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/release`), request);
    }
    /** POST admin/campaigns/{id}/reassign (Assign): move contacts from one caller to another. */
    async reassignContacts(campaignId, request) {
        return unwrap(await this.reassignContactsRaw(campaignId, request), 'Failed to reassign the contacts');
    }
    reassignContactsRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/reassign`), request);
    }
    /** GET admin/campaigns/{id}/imports (Read) */
    async listCampaignImports(campaignId) {
        return listOrEmpty(await this.listCampaignImportsRaw(campaignId));
    }
    listCampaignImportsRaw(campaignId) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/imports`));
    }
    /** POST admin/campaigns/{id}/imports (Import): open an import batch, then send rows with addImportRows. */
    async startImport(campaignId, request = {}) {
        return unwrap(await this.startImportRaw(campaignId, request), 'Failed to start the import');
    }
    startImportRaw(campaignId, request = {}) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/imports`), request);
    }
    // ---- imports ----
    /** GET admin/imports/{batchId} (Read) */
    async getImport(batchId) {
        return unwrap(await this.getImportRaw(batchId), `Import ${batchId} not found`);
    }
    getImportRaw(batchId) {
        return this.http.get(this.adminUrl(`imports/${batchId}`));
    }
    /** POST admin/imports/{batchId}/rows (Import): one chunk of at most 500 rows; `isLastChunk` finishes matching. */
    async addImportRows(batchId, request) {
        return unwrap(await this.addImportRowsRaw(batchId, request), 'Failed to add the rows');
    }
    addImportRowsRaw(batchId, request) {
        return this.http.post(this.adminUrl(`imports/${batchId}/rows`), request);
    }
    /** GET admin/imports/{batchId}/rows?matchStatusId=&undecidedOnly=&page=&pageSize= (Read) */
    async listImportRows(batchId, query = {}) {
        return unwrap(await this.listImportRowsRaw(batchId, query), 'Import rows not found');
    }
    listImportRowsRaw(batchId, query = {}) {
        return this.http.get(this.adminUrl(`imports/${batchId}/rows`, {
            matchStatusId: query.matchStatusId,
            undecidedOnly: query.undecidedOnly,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** PUT admin/imports/{batchId}/rows/{rowId} (Import): pick a candidate, mark new (create a member) or skip. */
    async decideImportRow(batchId, rowId, request) {
        return unwrap(await this.decideImportRowRaw(batchId, rowId, request), 'Failed to save the decision');
    }
    decideImportRowRaw(batchId, rowId, request) {
        return this.http.put(this.adminUrl(`imports/${batchId}/rows/${rowId}`), request);
    }
    /** POST admin/imports/{batchId}/bulk-decision (Import): one decision for every row in a match status. */
    async bulkImportDecision(batchId, request) {
        return unwrap(await this.bulkImportDecisionRaw(batchId, request), 'Failed to apply the decision');
    }
    bulkImportDecisionRaw(batchId, request) {
        return this.http.post(this.adminUrl(`imports/${batchId}/bulk-decision`), request);
    }
    /** POST admin/imports/{batchId}/commit (Import): add the reviewed rows to the campaign. */
    async commitImport(batchId, request = {}) {
        return unwrap(await this.commitImportRaw(batchId, request), 'Failed to add the people');
    }
    commitImportRaw(batchId, request = {}) {
        return this.http.post(this.adminUrl(`imports/${batchId}/commit`), request);
    }
    /** POST admin/imports/{batchId}/cancel (Import) */
    async cancelImport(batchId) {
        return unwrap(await this.cancelImportRaw(batchId), 'Failed to cancel the import');
    }
    cancelImportRaw(batchId) {
        return this.http.post(this.adminUrl(`imports/${batchId}/cancel`), {});
    }
    // ---- list sources (Stage 2) ----
    /** GET admin/sources/pco-lists?search= (Import): Planning Centre People lists, with their count and refreshed date. */
    async listPcoSources(search) {
        return listOrEmpty(await this.listPcoSourcesRaw(search));
    }
    listPcoSourcesRaw(search) {
        return this.http.get(this.adminUrl('sources/pco-lists', { search }));
    }
    /**
     * GET admin/sources/pco-forms?search=&includeArchived= (Import): Planning Centre People forms (`count` = submissions,
     * `date` = made on, `isArchived`; closed or archived shows in `detail`).
     */
    async listPcoFormSources(search, includeArchived) {
        return listOrEmpty(await this.listPcoFormSourcesRaw(search, includeArchived));
    }
    listPcoFormSourcesRaw(search, includeArchived) {
        return this.http.get(this.adminUrl('sources/pco-forms', { search, includeArchived: includeArchived || undefined }));
    }
    /**
     * GET admin/sources/pco-forms/{formId}/preview?submittedSince=&max= (Import): how many people pulling the form would
     * bring in (people, with a phone, already CCMS members). `submittedSince` is a church-local yyyy-MM-dd; `max` is the
     * pull's Max people (default 2000): counting stops there (`isCapped`). Nothing is saved; a big form takes a few
     * seconds (Planning Centre is read 100 submissions at a time).
     */
    async previewPcoFormSource(formId, submittedSince, max) {
        return unwrap(await this.previewPcoFormSourceRaw(formId, submittedSince, max), 'Failed to count the people');
    }
    previewPcoFormSourceRaw(formId, submittedSince, max) {
        return this.http.get(this.adminUrl(`sources/pco-forms/${encodeURIComponent(formId)}/preview`, { submittedSince: submittedSince || undefined, max: max ?? undefined }));
    }
    /** GET admin/sources/forms?search= (Import): CCMS forms, incl. event sign-ups (`statuses` = entry statuses to filter on). */
    async listFormSources(search) {
        return listOrEmpty(await this.listFormSourcesRaw(search));
    }
    listFormSourcesRaw(search) {
        return this.http.get(this.adminUrl('sources/forms', { search }));
    }
    /** GET admin/sources/course-dates?includePast= (Import): Acts Care course dates (`statuses` = registration statuses). */
    async listCourseDateSources(includePast) {
        return listOrEmpty(await this.listCourseDateSourcesRaw(includePast));
    }
    listCourseDateSourcesRaw(includePast) {
        return this.http.get(this.adminUrl('sources/course-dates', { includePast }));
    }
    /**
     * POST admin/campaigns/{id}/imports/from-source (Import): the server pulls the people (CCMS members, a Planning
     * Centre List or form, form entries or Acts Care registrations) into a new list that comes back Ready for review.
     * A 503 `member_service_unavailable` is worth a retry.
     */
    async importFromSource(campaignId, request) {
        return unwrap(await this.importFromSourceRaw(campaignId, request), 'Failed to pull the people');
    }
    importFromSourceRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/imports/from-source`), request);
    }
    // ---- Planning Centre linking + sync jobs (Stage 2) ----
    /**
     * POST admin/campaigns/{id}/link-pco (Import): create missing CCMS members and find / create / link every
     * person on the campaign in Planning Centre. Queued and paced; returns the sync job to follow.
     */
    async linkCampaignPco(campaignId, request = {}) {
        return unwrap(await this.linkCampaignPcoRaw(campaignId, request), 'Failed to start Planning Centre linking');
    }
    linkCampaignPcoRaw(campaignId, request = {}) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/link-pco`), request);
    }
    /** GET admin/sync-jobs?campaignId=&openOnly= (Read) */
    async listSyncJobs(query = {}) {
        return listOrEmpty(await this.listSyncJobsRaw(query));
    }
    listSyncJobsRaw(query = {}) {
        return this.http.get(this.adminUrl('sync-jobs', { campaignId: query.campaignId, openOnly: query.openOnly }));
    }
    /** GET admin/sync-jobs/{id}?statusId=&page=&pageSize= (Read): the job and one page of its items. */
    async getSyncJob(syncJobId, query = {}) {
        return unwrap(await this.getSyncJobRaw(syncJobId, query), `Sync job ${syncJobId} not found`);
    }
    getSyncJobRaw(syncJobId, query = {}) {
        return this.http.get(this.adminUrl(`sync-jobs/${syncJobId}`, { statusId: query.statusId, page: query.page, pageSize: query.pageSize }));
    }
    /** POST admin/sync-jobs/{id}/retry (Import): failed items queued again (conflicts are never retried). */
    async retrySyncJob(syncJobId) {
        return unwrap(await this.retrySyncJobRaw(syncJobId), 'Failed to retry the job');
    }
    retrySyncJobRaw(syncJobId) {
        return this.http.post(this.adminUrl(`sync-jobs/${syncJobId}/retry`), {});
    }
    /** POST admin/sync-jobs/{id}/cancel (Import): queued items cancelled. */
    async cancelSyncJob(syncJobId) {
        return unwrap(await this.cancelSyncJobRaw(syncJobId), 'Failed to cancel the job');
    }
    cancelSyncJobRaw(syncJobId) {
        return this.http.post(this.adminUrl(`sync-jobs/${syncJobId}/cancel`), {});
    }
    /** POST admin/people/{personId}/pco/find (Contacts.Edit): Planning Centre candidates for a person (needs a CCMS member). */
    async findPersonPco(personId) {
        return unwrap(await this.findPersonPcoRaw(personId), 'Failed to search Planning Centre');
    }
    findPersonPcoRaw(personId) {
        return this.http.post(this.adminUrl(`people/${personId}/pco/find`), {});
    }
    /** POST admin/people/{personId}/pco/link (Contacts.Edit): link the person's CCMS member to this Planning Centre person. */
    async linkPersonPco(personId, request) {
        return unwrap(await this.linkPersonPcoRaw(personId, request), 'Failed to link the Planning Centre person');
    }
    linkPersonPcoRaw(personId, request) {
        return this.http.post(this.adminUrl(`people/${personId}/pco/link`), request);
    }
    /**
     * POST admin/people/{personId}/pco/create (Contacts.Edit): find by exact email / phone, else create; then link.
     * Several exact matches come back as needsChoice + candidates (nothing created); link one, or call again with
     * createEvenIfMatched when none of them is this person.
     */
    async createPersonPco(personId, request = {}) {
        return unwrap(await this.createPersonPcoRaw(personId, request), 'Failed to create the Planning Centre person');
    }
    createPersonPcoRaw(personId, request = {}) {
        return this.http.post(this.adminUrl(`people/${personId}/pco/create`), request);
    }
    // ---- contacts ----
    /** GET admin/contacts (Read): contacts across campaigns. */
    async listContacts(query = {}) {
        return unwrap(await this.listContactsRaw(query), 'Contacts not found');
    }
    listContactsRaw(query = {}) {
        return this.http.get(this.adminUrl('contacts', CallCentreAdminService.contactParams(query)));
    }
    /** GET admin/contacts/{id} (Read): full detail (full phone, attempts, answers, handoffs, journey). */
    async getContact(contactId) {
        return unwrap(await this.getContactRaw(contactId), `Contact ${contactId} not found`);
    }
    getContactRaw(contactId) {
        return this.http.get(this.adminUrl(`contacts/${contactId}`));
    }
    /** PUT admin/contacts/{id} (Contacts.Edit) */
    async updateContact(contactId, request) {
        return unwrap(await this.updateContactRaw(contactId, request), 'Failed to save the contact');
    }
    updateContactRaw(contactId, request) {
        return this.http.put(this.adminUrl(`contacts/${contactId}`), request);
    }
    /** POST admin/contacts/{id}/remove (Contacts.Edit): status Removed. */
    async removeContact(contactId) {
        return unwrap(await this.removeContactRaw(contactId), 'Failed to remove the contact');
    }
    removeContactRaw(contactId) {
        return this.http.post(this.adminUrl(`contacts/${contactId}/remove`), {});
    }
    /** POST admin/contacts/{id}/reopen (Contacts.Edit) */
    async reopenContact(contactId) {
        return unwrap(await this.reopenContactRaw(contactId), 'Failed to reopen the contact');
    }
    reopenContactRaw(contactId) {
        return this.http.post(this.adminUrl(`contacts/${contactId}/reopen`), {});
    }
    // ---- do not call ----
    /** GET admin/do-not-call?page=&pageSize=&search= (Read) */
    async listDoNotCall(query = {}) {
        return unwrap(await this.listDoNotCallRaw(query), 'Do-not-call list not found');
    }
    listDoNotCallRaw(query = {}) {
        return this.http.get(this.adminUrl('do-not-call', { page: query.page, pageSize: query.pageSize, search: query.search }));
    }
    /** POST admin/do-not-call (Contacts.Edit) */
    async addDoNotCall(request) {
        return unwrap(await this.addDoNotCallRaw(request), 'Failed to add the number');
    }
    addDoNotCallRaw(request) {
        return this.http.post(this.adminUrl('do-not-call'), request);
    }
    /** DELETE admin/do-not-call/{id} (Contacts.Edit) */
    async removeDoNotCall(doNotCallId) {
        return okOrSuccess(await this.removeDoNotCallRaw(doNotCallId));
    }
    removeDoNotCallRaw(doNotCallId) {
        return this.http.delete(this.adminUrl(`do-not-call/${doNotCallId}`));
    }
    // ---- detail changes ----
    /** GET admin/detail-changes?statusId=&page=&pageSize= (Read) */
    async listDetailChanges(query = {}) {
        return unwrap(await this.listDetailChangesRaw(query), 'Detail changes not found');
    }
    listDetailChangesRaw(query = {}) {
        return this.http.get(this.adminUrl('detail-changes', { statusId: query.statusId, page: query.page, pageSize: query.pageSize }));
    }
    /** POST admin/detail-changes/review (Contacts.Edit): approve or reject a set of proposed changes. */
    async reviewDetailChanges(request) {
        return unwrap(await this.reviewDetailChangesRaw(request), 'Failed to review the changes');
    }
    reviewDetailChangesRaw(request) {
        return this.http.post(this.adminUrl('detail-changes/review'), request);
    }
    // ---- journey (DA, E1 to E5) ----
    /** GET admin/journey/board (Read): funnel plus the latest entries. */
    async getJourneyBoard() {
        return unwrap(await this.getJourneyBoardRaw(), 'Journey board not found');
    }
    getJourneyBoardRaw() {
        return this.http.get(this.adminUrl('journey/board'));
    }
    /** GET admin/journey/steps (Read) */
    async getJourneySteps() {
        return listOrEmpty(await this.getJourneyStepsRaw());
    }
    getJourneyStepsRaw() {
        return this.http.get(this.adminUrl('journey/steps'));
    }
    /** POST admin/journey/steps (Settings.Manage) */
    async createJourneyStep(request) {
        return unwrap(await this.createJourneyStepRaw(request), 'Failed to create the journey step');
    }
    createJourneyStepRaw(request) {
        return this.http.post(this.adminUrl('journey/steps'), request);
    }
    /** PUT admin/journey/steps/{stepId} (Settings.Manage) */
    async updateJourneyStep(stepId, request) {
        return unwrap(await this.updateJourneyStepRaw(stepId, request), 'Failed to save the journey step');
    }
    updateJourneyStepRaw(stepId, request) {
        return this.http.put(this.adminUrl(`journey/steps/${stepId}`), request);
    }
    /**
     * GET admin/journey/steps/{stepId}/course-dates (Read): upcoming dates of the Acts Care course the step books onto
     * (BookingTargetTypeID ActsCareCourse, BookingTargetRef = CourseID). 400 when the step is not linked to a course;
     * 503 `course_service_unavailable` when Acts Care can't be reached (book the date by hand for now).
     */
    async getStepCourseDates(stepId) {
        return listOrEmpty(await this.getStepCourseDatesRaw(stepId));
    }
    getStepCourseDatesRaw(stepId) {
        return this.http.get(this.adminUrl(`journey/steps/${stepId}/course-dates`));
    }
    /** PUT admin/journey/people/{personId} (Journey.Manage): record, correct or back-fill a step for a call-centre person. */
    async recordPersonJourneyStep(personId, request) {
        return unwrap(await this.recordPersonJourneyStepRaw(personId, request), 'Failed to record the journey step');
    }
    recordPersonJourneyStepRaw(personId, request) {
        return this.http.put(this.adminUrl(`journey/people/${personId}`), request);
    }
    /** PUT admin/journey/members/{memberId} (Journey.Manage): same for a CCMS member (creates the person if needed). */
    async recordMemberJourneyStep(memberId, request) {
        return unwrap(await this.recordMemberJourneyStepRaw(memberId, request), 'Failed to record the journey step');
    }
    recordMemberJourneyStepRaw(memberId, request) {
        return this.http.put(this.adminUrl(`journey/members/${memberId}`), request);
    }
    /** DELETE admin/journey/entries/{entryId} (Journey.Manage) */
    async deleteJourneyEntry(entryId) {
        return okOrSuccess(await this.deleteJourneyEntryRaw(entryId));
    }
    deleteJourneyEntryRaw(entryId) {
        return this.http.delete(this.adminUrl(`journey/entries/${entryId}`));
    }
    /**
     * GET api/callcentre/journey/members/{memberId} (Member.Read OR CallCentre.Read): the member profile's
     * Journey card. Not under admin/, so any console user with either right can call it.
     */
    async getMemberJourney(memberId) {
        return unwrap(await this.getMemberJourneyRaw(memberId), `Journey for member ${memberId} not found`);
    }
    getMemberJourneyRaw(memberId) {
        return this.http.get(this.buildUrl(`journey/members/${memberId}`));
    }
    // ---- handoffs ----
    /** GET admin/handoffs (Read) */
    async listHandoffs(query = {}) {
        return unwrap(await this.listHandoffsRaw(query), 'Handoffs not found');
    }
    listHandoffsRaw(query = {}) {
        return this.http.get(this.adminUrl('handoffs', {
            statusId: query.statusId,
            typeId: query.typeId,
            campaignId: query.campaignId,
            ministryId: query.ministryId,
            openOnly: query.openOnly,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** POST admin/handoffs/{id}/status (Handoffs.Manage) */
    async setHandoffStatus(handoffId, request) {
        return unwrap(await this.setHandoffStatusRaw(handoffId, request), 'Failed to update the handoff');
    }
    setHandoffStatusRaw(handoffId, request) {
        return this.http.post(this.adminUrl(`handoffs/${handoffId}/status`), request);
    }
    /**
     * POST admin/handoffs/{id}/send-to-care (Handoffs.Manage): creates the Acts Care request for a Care referral now
     * (the person agreed and is a CCMS member). `integrationMessage` / `integrationDone` say what happened.
     */
    async sendHandoffToCare(handoffId) {
        return unwrap(await this.sendHandoffToCareRaw(handoffId), 'Failed to send the referral to Acts Care');
    }
    sendHandoffToCareRaw(handoffId) {
        return this.http.post(this.adminUrl(`handoffs/${handoffId}/send-to-care`), {});
    }
    /** POST admin/handoffs/{id}/reassign (Handoffs.Manage) */
    async reassignHandoff(handoffId, request) {
        return unwrap(await this.reassignHandoffRaw(handoffId, request), 'Failed to reassign the handoff');
    }
    reassignHandoffRaw(handoffId, request) {
        return this.http.post(this.adminUrl(`handoffs/${handoffId}/reassign`), request);
    }
    // ---- ministry owners ----
    /** GET admin/ministry-owners (Read) */
    async getMinistryOwners() {
        return listOrEmpty(await this.getMinistryOwnersRaw());
    }
    getMinistryOwnersRaw() {
        return this.http.get(this.adminUrl('ministry-owners'));
    }
    /** PUT admin/ministry-owners/{ministryId} (Handoffs.Manage): set (or replace) the ministry's owner. */
    async saveMinistryOwner(ministryId, request) {
        return unwrap(await this.saveMinistryOwnerRaw(ministryId, request), 'Failed to save the ministry owner');
    }
    saveMinistryOwnerRaw(ministryId, request) {
        return this.http.put(this.adminUrl(`ministry-owners/${ministryId}`), request);
    }
    /** DELETE admin/ministry-owners/{ministryId} (Handoffs.Manage) */
    async removeMinistryOwner(ministryId) {
        return okOrSuccess(await this.removeMinistryOwnerRaw(ministryId));
    }
    removeMinistryOwnerRaw(ministryId) {
        return this.http.delete(this.adminUrl(`ministry-owners/${ministryId}`));
    }
    // ---- callers ----
    /** GET admin/callers (Read) */
    async getCallers() {
        return listOrEmpty(await this.getCallersRaw());
    }
    getCallersRaw() {
        return this.http.get(this.adminUrl('callers'));
    }
    /** POST admin/callers (Assign): add or update a caller (grantCallerRole defaults to true). */
    async saveCaller(request) {
        return unwrap(await this.saveCallerRaw(request), 'Failed to save the caller');
    }
    saveCallerRaw(request) {
        return this.http.post(this.adminUrl('callers'), request);
    }
    /** DELETE admin/callers/{userId}?revokeRole= (Assign): deactivate; revokeRole also removes CallCentreCaller. */
    async removeCaller(userId, revokeRole) {
        return okOrSuccess(await this.removeCallerRaw(userId, revokeRole));
    }
    removeCallerRaw(userId, revokeRole) {
        return this.http.delete(this.adminUrl(`callers/${encodeURIComponent(userId)}`, { revokeRole }));
    }
    // ---- caller teams (Stage 5) ----
    /** GET admin/teams?includeInactive= (Read) */
    async listTeams(includeInactive) {
        return listOrEmpty(await this.listTeamsRaw(includeInactive));
    }
    listTeamsRaw(includeInactive) {
        return this.http.get(this.adminUrl('teams', { includeInactive }));
    }
    /** POST admin/teams (Assign) */
    async createTeam(request) {
        return unwrap(await this.createTeamRaw(request), 'Failed to save the team');
    }
    createTeamRaw(request) {
        return this.http.post(this.adminUrl('teams'), request);
    }
    /** PUT admin/teams/{teamId} (Assign) */
    async updateTeam(teamId, request) {
        return unwrap(await this.updateTeamRaw(teamId, request), 'Failed to save the team');
    }
    updateTeamRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}`), request);
    }
    /** PUT admin/teams/{teamId}/members (Assign): the full member list (callers only; add people as callers first). */
    async setTeamMembers(teamId, request) {
        return unwrap(await this.setTeamMembersRaw(teamId, request), 'Failed to save the team members');
    }
    setTeamMembersRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}/members`), request);
    }
    /** DELETE admin/teams/{teamId} (Assign) */
    async deleteTeam(teamId) {
        return okOrSuccess(await this.deleteTeamRaw(teamId));
    }
    deleteTeamRaw(teamId) {
        return this.http.delete(this.adminUrl(`teams/${teamId}`));
    }
    // ---- reminders (the signed-in admin's own) ----
    /** GET admin/reminders?includeRead= (Read) */
    async getReminders(includeRead) {
        return unwrap(await this.getRemindersRaw(includeRead), 'Reminders not found');
    }
    getRemindersRaw(includeRead) {
        return this.http.get(this.adminUrl('reminders', { includeRead }));
    }
    /** POST admin/reminders/{id}/read (Read) */
    async markReminderRead(reminderId) {
        return okOrSuccess(await this.markReminderReadRaw(reminderId));
    }
    markReminderReadRaw(reminderId) {
        return this.http.post(this.adminUrl(`reminders/${reminderId}/read`), {});
    }
    /** POST admin/reminders/{id}/dismiss (Read) */
    async dismissReminder(reminderId) {
        return okOrSuccess(await this.dismissReminderRaw(reminderId));
    }
    dismissReminderRaw(reminderId) {
        return this.http.post(this.adminUrl(`reminders/${reminderId}/dismiss`), {});
    }
    // ---- settings, outcomes, campaign types ----
    /** GET admin/settings (Read) */
    async getSettings() {
        return unwrap(await this.getSettingsRaw(), 'Call centre settings not found');
    }
    getSettingsRaw() {
        return this.http.get(this.adminUrl('settings'));
    }
    /** PUT admin/settings (Settings.Manage): the whole form. */
    async saveSettings(request) {
        return unwrap(await this.saveSettingsRaw(request), 'Failed to save the settings');
    }
    saveSettingsRaw(request) {
        return this.http.put(this.adminUrl('settings'), request);
    }
    /** GET admin/settings/notification-topics (Read): per-topic email / SMS / WhatsApp switches (in-app always shows). */
    async getNotificationTopics() {
        return listOrEmpty(await this.getNotificationTopicsRaw());
    }
    getNotificationTopicsRaw() {
        return this.http.get(this.adminUrl('settings/notification-topics'));
    }
    /** PUT admin/settings/notification-topics (Settings.Manage): only the topics listed change; returns every topic. */
    async saveNotificationTopics(request) {
        return listOrEmpty(await this.saveNotificationTopicsRaw(request));
    }
    saveNotificationTopicsRaw(request) {
        return this.http.put(this.adminUrl('settings/notification-topics'), request);
    }
    /**
     * POST admin/settings/notifications/test (Settings.Manage): sends a test to the signed-in admin on the chosen
     * channels, even before the channels are switched on. `message` says what happened.
     */
    async sendTestNotification(request = {}) {
        return unwrap(await this.sendTestNotificationRaw(request), 'Failed to send the test');
    }
    sendTestNotificationRaw(request = {}) {
        return this.http.post(this.adminUrl('settings/notifications/test'), request);
    }
    /**
     * 271j: GET admin/settings/member-fields (Settings.Manage): the member fields callers may add when missing and / or
     * update (built-in fields, contact list uses, non-sensitive custom fields) with both switches. `catalogueAvailable`
     * false = Member Management can't be reached: `fields` lists the saved rules only and saving is off.
     */
    async getMemberFieldSettings() {
        return unwrap(await this.getMemberFieldSettingsRaw(), 'Member field settings not found');
    }
    getMemberFieldSettingsRaw() {
        return this.http.get(this.adminUrl('settings/member-fields'));
    }
    /**
     * 271j: PUT admin/settings/member-fields (Settings.Manage): only the fields sent change; both switches off = no longer
     * offered. 400 `member_field_not_allowed` for a sensitive, switched-off or unknown field; 503
     * `member_service_unavailable` while Member Management is down.
     */
    async saveMemberFieldSettings(request) {
        return unwrap(await this.saveMemberFieldSettingsRaw(request), 'Failed to save the member fields');
    }
    saveMemberFieldSettingsRaw(request) {
        return this.http.put(this.adminUrl('settings/member-fields'), request);
    }
    // ---------------------------------------------------------------- 271m: results and messages
    /** GET admin/campaigns/{id}/results (Read): every call result with whether this campaign's callers see it, and its messages. */
    async getCampaignResults(campaignId) {
        return unwrap(await this.getCampaignResultsRaw(campaignId), 'Call results not found');
    }
    getCampaignResultsRaw(campaignId) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/results`));
    }
    /** PUT admin/campaigns/{id}/results (Campaigns.Manage): show or hide results (at least one stays shown). */
    async saveCampaignResults(campaignId, request) {
        return unwrap(await this.saveCampaignResultsRaw(campaignId, request), 'Failed to save the call results');
    }
    saveCampaignResultsRaw(campaignId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}/results`), request);
    }
    /** POST admin/campaigns/{id}/messages (Campaigns.Manage): a message to the person after a result. */
    async addCampaignMessage(campaignId, request) {
        return unwrap(await this.addCampaignMessageRaw(campaignId, request), 'Failed to add the message');
    }
    addCampaignMessageRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/messages`), request);
    }
    /** PUT admin/campaigns/{id}/messages/{messageId} (Campaigns.Manage) */
    async saveCampaignMessage(campaignId, messageId, request) {
        return unwrap(await this.saveCampaignMessageRaw(campaignId, messageId, request), 'Failed to save the message');
    }
    saveCampaignMessageRaw(campaignId, messageId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}/messages/${messageId}`), request);
    }
    /** DELETE admin/campaigns/{id}/messages/{messageId} (Campaigns.Manage) */
    async deleteCampaignMessage(campaignId, messageId) {
        return okOrSuccess(await this.deleteCampaignMessageRaw(campaignId, messageId));
    }
    deleteCampaignMessageRaw(campaignId, messageId) {
        return this.http.delete(this.adminUrl(`campaigns/${campaignId}/messages/${messageId}`));
    }
    /** POST admin/campaigns/{id}/messages/suggested (Campaigns.Manage): the library's presets for results with no message yet, switched off. */
    async addSuggestedMessages(campaignId) {
        return unwrap(await this.addSuggestedMessagesRaw(campaignId), 'Failed to add the suggested messages');
    }
    addSuggestedMessagesRaw(campaignId) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/messages/suggested`), {});
    }
    /** GET admin/settings/message-presets (Read): the message library, hidden ones too. */
    async listMessagePresets() {
        return unwrap(await this.listMessagePresetsRaw(), 'Messages not found');
    }
    listMessagePresetsRaw() {
        return this.http.get(this.adminUrl('settings/message-presets'));
    }
    /** POST admin/settings/message-presets (Settings.Manage) */
    async addMessagePreset(request) {
        return unwrap(await this.addMessagePresetRaw(request), 'Failed to add the message');
    }
    addMessagePresetRaw(request) {
        return this.http.post(this.adminUrl('settings/message-presets'), request);
    }
    /** PUT admin/settings/message-presets/{presetId} (Settings.Manage) */
    async saveMessagePreset(presetId, request) {
        return unwrap(await this.saveMessagePresetRaw(presetId, request), 'Failed to save the message');
    }
    saveMessagePresetRaw(presetId, request) {
        return this.http.put(this.adminUrl(`settings/message-presets/${presetId}`), request);
    }
    /** DELETE admin/settings/message-presets/{presetId} (Settings.Manage): your own messages only (built-in ones can be hidden). */
    async deleteMessagePreset(presetId) {
        return okOrSuccess(await this.deleteMessagePresetRaw(presetId));
    }
    deleteMessagePresetRaw(presetId) {
        return this.http.delete(this.adminUrl(`settings/message-presets/${presetId}`));
    }
    /** GET admin/contacts/{id}/messages (Read): what was sent (or not) to the contact after their calls, newest first. */
    async listContactMessages(contactId) {
        return unwrap(await this.listContactMessagesRaw(contactId), 'Messages not found');
    }
    listContactMessagesRaw(contactId) {
        return this.http.get(this.adminUrl(`contacts/${contactId}/messages`));
    }
    /** POST admin/outcomes (Settings.Manage) */
    async createOutcome(request) {
        return unwrap(await this.createOutcomeRaw(request), 'Failed to create the outcome');
    }
    createOutcomeRaw(request) {
        return this.http.post(this.adminUrl('outcomes'), request);
    }
    /** PUT admin/outcomes/{id} (Settings.Manage) */
    async updateOutcome(outcomeId, request) {
        return unwrap(await this.updateOutcomeRaw(outcomeId, request), 'Failed to save the outcome');
    }
    updateOutcomeRaw(outcomeId, request) {
        return this.http.put(this.adminUrl(`outcomes/${outcomeId}`), request);
    }
    /** POST admin/campaign-types (Settings.Manage) */
    async createCampaignType(request) {
        return unwrap(await this.createCampaignTypeRaw(request), 'Failed to create the campaign type');
    }
    createCampaignTypeRaw(request) {
        return this.http.post(this.adminUrl('campaign-types'), request);
    }
    /** PUT admin/campaign-types/{id} (Settings.Manage) */
    async updateCampaignType(campaignTypeId, request) {
        return unwrap(await this.updateCampaignTypeRaw(campaignTypeId, request), 'Failed to save the campaign type');
    }
    updateCampaignTypeRaw(campaignTypeId, request) {
        return this.http.put(this.adminUrl(`campaign-types/${campaignTypeId}`), request);
    }
    // ---- Stage 3: phone system (Grandstream UCM click-to-call) ----
    /** GET admin/telephony (Read): the phone system settings (never the password; `hasPassword` says one is stored). */
    async getTelephony() {
        return unwrap(await this.getTelephonyRaw(), 'Phone system settings not found');
    }
    getTelephonyRaw() {
        return this.http.get(this.adminUrl('telephony'));
    }
    /** PUT admin/telephony (Telephony.Manage): the whole form; a blank password keeps the stored one. */
    async saveTelephony(request) {
        return unwrap(await this.saveTelephonyRaw(request), 'Failed to save the phone system settings');
    }
    saveTelephonyRaw(request) {
        return this.http.put(this.adminUrl('telephony'), request);
    }
    /** POST admin/telephony/test (Telephony.Manage): sign in to the UCM with the saved settings. */
    async testTelephony() {
        return unwrap(await this.testTelephonyRaw(), 'Failed to test the phone system');
    }
    testTelephonyRaw() {
        return this.http.post(this.adminUrl('telephony/test'), {});
    }
    /** POST admin/telephony/diagnose (Telephony.Manage): DNS, TCP, TLS, API and sign in, step by step. */
    async diagnoseTelephony() {
        return unwrap(await this.diagnoseTelephonyRaw(), 'Failed to diagnose the phone system');
    }
    diagnoseTelephonyRaw() {
        return this.http.post(this.adminUrl('telephony/diagnose'), {});
    }
    /** GET admin/telephony/cdr?unmatchedOnly=&page=&pageSize= (Read): call records read from the UCM. */
    async listCdr(query = {}) {
        return unwrap(await this.listCdrRaw(query), 'Call records not found');
    }
    listCdrRaw(query = {}) {
        return this.http.get(this.adminUrl('telephony/cdr', { unmatchedOnly: query.unmatchedOnly, page: query.page, pageSize: query.pageSize }));
    }
    /** POST admin/telephony/cdr/sync (Telephony.Manage): read call records now; `affected` = records read. */
    async syncCdr() {
        return unwrap(await this.syncCdrRaw(), 'Failed to read the call records');
    }
    syncCdrRaw() {
        return this.http.post(this.adminUrl('telephony/cdr/sync'), {});
    }
    /** GET admin/stations (Read): desk phones, with who is using each now (`inUseByName`). */
    async listStations() {
        return listOrEmpty(await this.listStationsRaw());
    }
    listStationsRaw() {
        return this.http.get(this.adminUrl('stations'));
    }
    /** POST admin/stations (Telephony.Manage) */
    async createStation(request) {
        return unwrap(await this.createStationRaw(request), 'Failed to add the desk phone');
    }
    createStationRaw(request) {
        return this.http.post(this.adminUrl('stations'), request);
    }
    /** PUT admin/stations/{id} (Telephony.Manage) */
    async updateStation(stationId, request) {
        return unwrap(await this.updateStationRaw(stationId, request), 'Failed to save the desk phone');
    }
    updateStationRaw(stationId, request) {
        return this.http.put(this.adminUrl(`stations/${stationId}`), request);
    }
    /** DELETE admin/stations/{id} (Telephony.Manage): switches the desk phone off (it is kept). */
    async removeStation(stationId) {
        return okOrSuccess(await this.removeStationRaw(stationId));
    }
    removeStationRaw(stationId) {
        return this.http.delete(this.adminUrl(`stations/${stationId}`));
    }
    // ---- Stage 4: journey integrations (Acts Care completions, Planning Centre sync targets) ----
    /** GET admin/sources/courses (Read): Acts Care courses for a journey step's booking target (id = the CourseID). */
    async listCourseSources() {
        return listOrEmpty(await this.listCourseSourcesRaw());
    }
    listCourseSourcesRaw() {
        return this.http.get(this.adminUrl('sources/courses'));
    }
    /**
     * POST admin/journey/steps/{stepId}/backfill?sinceYear= (Journey.Manage): records earlier Acts Care completions
     * of the step's linked course as Completed (idempotent). `affected` = entries recorded, `message` says what happened.
     */
    async backfillJourneyStep(stepId, sinceYear) {
        return unwrap(await this.backfillJourneyStepRaw(stepId, sinceYear), 'Failed to back-fill the journey step');
    }
    backfillJourneyStepRaw(stepId, sinceYear) {
        return this.http.post(this.adminUrl(`journey/steps/${stepId}/backfill`, { sinceYear }), {});
    }
    /** GET admin/pco/field-definitions (Settings.Manage): Planning Centre custom fields (Custom field mode). */
    async listPcoFieldDefinitions() {
        return listOrEmpty(await this.listPcoFieldDefinitionsRaw());
    }
    listPcoFieldDefinitionsRaw() {
        return this.http.get(this.adminUrl('pco/field-definitions'));
    }
    /** GET admin/pco/workflows (Settings.Manage): Planning Centre workflows with their steps (Workflow card mode). */
    async listPcoWorkflows() {
        return listOrEmpty(await this.listPcoWorkflowsRaw());
    }
    listPcoWorkflowsRaw() {
        return this.http.get(this.adminUrl('pco/workflows'));
    }
    // ---- Stage 6: AI assist ----
    /** GET admin/ai/availability (Read): which AI helpers this admin can use right now (all false while AI is off). */
    async getAiAvailability() {
        return unwrap(await this.getAiAvailabilityRaw(), 'AI availability not found');
    }
    getAiAvailabilityRaw() {
        return this.http.get(this.adminUrl('ai/availability'));
    }
    /**
     * POST admin/ai/script-draft (Campaigns.Manage, scriptAssist on): a draft script from an existing campaign
     * (`campaignID`) or the form's name, type, goal and current script. Nothing is saved.
     */
    async aiScriptDraft(request) {
        return unwrap(await this.aiScriptDraftRaw(request), 'No draft came back');
    }
    aiScriptDraftRaw(request) {
        return this.http.post(this.adminUrl('ai/script-draft'), request);
    }
    /**
     * POST admin/imports/ai-map (Import, importAssist on): a suggested field per spreadsheet column from the headers
     * and up to 5 raw rows (the rows stay on the server; only value shapes reach the model). At most 60 columns.
     */
    async aiImportMap(request) {
        return unwrap(await this.aiImportMapRaw(request), 'No mapping came back');
    }
    aiImportMapRaw(request) {
        return this.http.post(this.adminUrl('imports/ai-map'), request);
    }
    /**
     * GET admin/ai/insights?days=&refresh= (Admin / CallCentreAdmin or CallCentre.AI.Use, insights on): themes, prayer
     * themes and coaching hints from the last `days` (1 to 30, default 7) of non-pastoral call notes. Cached for 6 hours
     * per period; `refresh: true` makes a new one.
     */
    async getAiInsights(days, refresh) {
        return unwrap(await this.getAiInsightsRaw(days, refresh), 'No insights came back');
    }
    getAiInsightsRaw(days, refresh) {
        return this.http.get(this.adminUrl('ai/insights', { days, refresh: refresh || undefined }));
    }
    // ------------------------------------------------------------------ conversation guides (271i)
    /** GET admin/campaigns/{id}/guide (Read): the campaign's guide with recorded call counts, or null when it has none. */
    async getCampaignGuide(campaignId) {
        return (await this.getCampaignGuideRaw(campaignId)).data ?? null;
    }
    getCampaignGuideRaw(campaignId) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/guide`));
    }
    /**
     * PUT admin/campaigns/{id}/guide (Campaigns.Manage): saves the WHOLE guide (creates it when there is none).
     * 400 `guide_invalid` lists problems as "steps.{key}.{field}: message"; 409 `guide_changed` when baseRevision is stale.
     */
    async saveCampaignGuide(campaignId, request) {
        return unwrap(await this.saveCampaignGuideRaw(campaignId, request), 'Failed to save the guide');
    }
    saveCampaignGuideRaw(campaignId, request) {
        return this.http.put(this.adminUrl(`campaigns/${campaignId}/guide`), request);
    }
    /**
     * POST admin/campaigns/{id}/guide/copy-starter (Campaigns.Manage): copies the campaign type's starter guide into the
     * campaign. `replace: false` (the wizard) keeps a guide the campaign already has (`copied: false`). 404
     * `no_starter_guide` when the type has none.
     */
    async copyCampaignGuideStarter(campaignId, request = {}) {
        return unwrap(await this.copyCampaignGuideStarterRaw(campaignId, request), 'Failed to copy the starter guide');
    }
    copyCampaignGuideStarterRaw(campaignId, request = {}) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/guide/copy-starter`), request);
    }
    /** GET admin/campaigns/{id}/guide/report?version= (Read): per step reach and the split per answer. */
    async getCampaignGuideReport(campaignId, version) {
        return unwrap(await this.getCampaignGuideReportRaw(campaignId, version), 'Failed to load the guide report');
    }
    getCampaignGuideReportRaw(campaignId, version) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/guide/report`, { version: version ?? undefined }));
    }
    /** GET admin/campaigns/{id}/guide/paths (Read): saved calls with the path they took, newest first. */
    async getCampaignGuidePaths(campaignId, query = {}) {
        return unwrap(await this.getCampaignGuidePathsRaw(campaignId, query), 'Failed to load the guide paths');
    }
    getCampaignGuidePathsRaw(campaignId, query = {}) {
        return this.http.get(this.adminUrl(`campaigns/${campaignId}/guide/paths`, {
            contactId: query.contactId ?? undefined,
            attemptId: query.attemptId ?? undefined,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** GET admin/guides/{guideId}/versions/{version} (Read): an older version, to read old paths against. */
    async getGuideVersion(guideId, version) {
        return unwrap(await this.getGuideVersionRaw(guideId, version), 'Guide version not found');
    }
    getGuideVersionRaw(guideId, version) {
        return this.http.get(this.adminUrl(`guides/${guideId}/versions/${version}`));
    }
    /** GET admin/guides/fact-options (Read, 271k): the member custom fields guide rules may look at (never sensitive ones). */
    async getGuideFactOptions() {
        return unwrap(await this.getGuideFactOptionsRaw(), 'Failed to load the member fields for guide rules');
    }
    getGuideFactOptionsRaw() {
        return this.http.get(this.adminUrl('guides/fact-options'));
    }
    /** GET admin/campaign-types/{id}/guide (Read): the type's starter guide, or null when it has none. */
    async getCampaignTypeGuide(campaignTypeId) {
        return (await this.getCampaignTypeGuideRaw(campaignTypeId)).data ?? null;
    }
    getCampaignTypeGuideRaw(campaignTypeId) {
        return this.http.get(this.adminUrl(`campaign-types/${campaignTypeId}/guide`));
    }
    /** PUT admin/campaign-types/{id}/guide (Campaigns.Manage): saves a starter. Campaigns that copied it keep their copy. */
    async saveCampaignTypeGuide(campaignTypeId, request) {
        return unwrap(await this.saveCampaignTypeGuideRaw(campaignTypeId, request), 'Failed to save the starter guide');
    }
    saveCampaignTypeGuideRaw(campaignTypeId, request) {
        return this.http.put(this.adminUrl(`campaign-types/${campaignTypeId}/guide`), request);
    }
    // ------------------------------------------------------------------ Build with AI (conversation guides, Claude on Bedrock)
    // Each POST starts a background job and answers at once; poll getGuideAiJob(jobId) every few seconds until `status` is
    // `done` (result.guide is the unsaved draft for the builder) or `failed` (errorCode ai_unavailable, ai_model_unavailable
    // or ai_failed; a failed job after the AI's second try still carries the draft with its problems). 503 ai_unavailable
    // straight away when the flag, the AI switch or CallCentre.AI.Use is off. All need CallCentre.Campaigns.Manage.
    /** POST admin/campaigns/{id}/guide/ai-draft: build a new guide for the campaign from a description. */
    async startCampaignGuideAiDraft(campaignId, request) {
        return unwrap(await this.startCampaignGuideAiDraftRaw(campaignId, request), 'Build with AI did not start');
    }
    startCampaignGuideAiDraftRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/guide/ai-draft`), request);
    }
    /** POST admin/campaigns/{id}/guide/ai-revise: change the guide as it is in the builder (unsaved changes included). */
    async startCampaignGuideAiRevise(campaignId, request) {
        return unwrap(await this.startCampaignGuideAiReviseRaw(campaignId, request), 'Ask AI did not start');
    }
    startCampaignGuideAiReviseRaw(campaignId, request) {
        return this.http.post(this.adminUrl(`campaigns/${campaignId}/guide/ai-revise`), request);
    }
    /** POST admin/campaign-types/{id}/guide/ai-draft: build a new starter guide for a campaign type. */
    async startCampaignTypeGuideAiDraft(campaignTypeId, request) {
        return unwrap(await this.startCampaignTypeGuideAiDraftRaw(campaignTypeId, request), 'Build with AI did not start');
    }
    startCampaignTypeGuideAiDraftRaw(campaignTypeId, request) {
        return this.http.post(this.adminUrl(`campaign-types/${campaignTypeId}/guide/ai-draft`), request);
    }
    /** POST admin/campaign-types/{id}/guide/ai-revise: change a starter guide as it is in the builder. */
    async startCampaignTypeGuideAiRevise(campaignTypeId, request) {
        return unwrap(await this.startCampaignTypeGuideAiReviseRaw(campaignTypeId, request), 'Ask AI did not start');
    }
    startCampaignTypeGuideAiReviseRaw(campaignTypeId, request) {
        return this.http.post(this.adminUrl(`campaign-types/${campaignTypeId}/guide/ai-revise`), request);
    }
    /** GET admin/guides/ai-jobs/{jobId}: a Build with AI job (only for whoever started it; 404 after 30 minutes). */
    async getGuideAiJob(jobId) {
        return unwrap(await this.getGuideAiJobRaw(jobId), 'That Build with AI job was not found');
    }
    getGuideAiJobRaw(jobId) {
        return this.http.get(this.adminUrl(`guides/ai-jobs/${encodeURIComponent(jobId)}`));
    }
}
CallCentreAdminService.DEFAULT_SERVICE_PATH = 'callcentre';
/** `admin/...` for console routes; `journey/members/{id}` (member profile card) sits beside it. */
CallCentreAdminService.BASE_PATH = 'api/callcentre';
//# sourceMappingURL=CallCentreAdminService.js.map